import React, { useMemo, ComponentProps } from 'react'

import StarRating from 'components/Common/StarRating'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import usePlaceDetails from 'tripPlanner/hooks/Google/usePlaceDetails'
import { PlaceResult } from 'tripPlanner/types/google'

const fields: (keyof PlaceResult)[] = ['rating', 'user_ratings_total']

type Size = 'S' | 'M'
const BODY_TEXT_SIZES: Record<
  Size,
  ComponentProps<typeof BodyText>['variant']
> = {
  S: 'small',
  M: 'medium',
}

const STAR_RATING_SIZES: Record<
  Size,
  ComponentProps<typeof StarRating>['size']
> = {
  S: 12,
  M: 'XS',
}

type Props = {
  placeId: string
  size: Size
  className?: string
  label?: string
}
const numberFormatter = new Intl.NumberFormat('en')

export const GooglePlaceRating = (props: Props) => {
  const { placeId, label, className, size } = props
  const { data: place, isLoading } = usePlaceDetails({ placeId, fields })
  const isMobileScreen = useIsMobileScreen()
  const breakdown = useMemo(() => {
    if (!place) return
    const { rating, user_ratings_total: ratingsCount } = place
    if (!rating) return
    const totalRatings = ratingsCount
      ? numberFormatter.format(ratingsCount)
      : undefined
    return {
      rating,
      totalRatings,
    }
  }, [place])
  if (!breakdown || isLoading) return null
  const { rating, totalRatings } = breakdown
  return (
    <Group gap={8} direction="vertical" className={className}>
      {label && (
        <BodyText variant="medium" weight="semi-bold">
          {label}
        </BodyText>
      )}
      <Group
        gap={8}
        direction="horizontal"
        horizontalAlign="start"
        verticalAlign="center"
      >
        <BodyText variant={BODY_TEXT_SIZES[size]}>{rating}</BodyText>
        <StarRating max={5} value={rating} size={STAR_RATING_SIZES[size]} />
        {totalRatings && (
          <BodyText variant={BODY_TEXT_SIZES[size]}>
            ({totalRatings}
            {isMobileScreen ? '' : ' Google reviews'})
          </BodyText>
        )}
      </Group>
    </Group>
  )
}
